import { Link } from '@tanstack/react-router';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { getCarnetSections, CARNET_DISCLAIMER } from '@/data/carnet-sections';

// ═══════════════════════════════════════════════════════════
// CarnetContent — Océanne (single source of truth).
// Réutilisé par CarnetModal (variant="modal") + routes/carnet.tsx (variant="page").
//
// Phase 27.81 refonte : passage du blog "articles à venir" vers carnet de
// ressources officielles AMG Québec. Les liens externes sont nofollow en
// modal (anti-spam) et noopener noreferrer en page SEO (E-E-A-T boost).
//
// Phase 27.85 SSG : data extraite vers src/data/carnet-sections.ts pour
// permettre Worker SSR (Schema.org enrichi + HTML template injection).
// Le composant et le Worker consomment la MÊME source de vérité.
// ═══════════════════════════════════════════════════════════

interface CarnetContentProps {
  variant: 'modal' | 'page';
  onClose?: () => void;
}

export function CarnetContent({ variant, onClose }: CarnetContentProps) {
  const { lang } = useLang();
  const text = ta(t.carnet, lang);
  const isFr = lang === 'fr';
  const isPage = variant === 'page';

  const linkRel = isPage ? 'noopener noreferrer' : 'noopener noreferrer nofollow';
  const sections = getCarnetSections(lang);
  const disclaimer = isFr ? CARNET_DISCLAIMER.fr : CARNET_DISCLAIMER.en;

  return (
    <div>
      {/* Body : 6 sections de ressources officielles */}
      <div className="space-y-12">
        {sections.map((section) => (
          <ScrollReveal key={section.id} as="section" {...{ id: section.id }}>
            <h2 className="mb-3 font-display text-2xl text-balance text-foreground md:text-3xl">
              {section.title}
            </h2>
            <p className="mb-6 font-sans text-base text-pretty hyphens-auto text-foreground/80">
              {section.intro}
            </p>
            <ul className="space-y-5">
              {section.entries.map((entry) => (
                <li key={entry.url} className="border-l-2 border-primary/40 pl-4">
                  <a
                    href={entry.url}
                    target="_blank"
                    rel={linkRel}
                    className="group inline-flex items-center gap-2 font-display text-lg text-foreground hover:text-primary transition-colors"
                  >
                    <span className="border-b border-transparent group-hover:border-primary/60 transition-colors">
                      {entry.name}
                    </span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />
                  </a>
                  <p className="mt-1 font-sans text-sm leading-relaxed text-foreground/75">
                    {entry.note}
                  </p>
                </li>
              ))}
            </ul>
          </ScrollReveal>
        ))}
      </div>

      {/* Disclaimer compliance */}
      <ScrollReveal as="p" className="mt-12 border-t border-border/60 pt-6 font-sans text-xs italic text-foreground/65">
        {disclaimer}
      </ScrollReveal>

      {/* CTA "Lire version complète" → page SEO (UNIQUEMENT en modal) */}
      {!isPage && (
        <div className="mt-10 border-t border-border/60 pt-8">
          <Link
            to="/carnet"
            onClick={onClose}
            className="group inline-flex items-center gap-2 font-sans text-sm font-bold uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
          >
            {text.modal_cta_full}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      )}

      {/* Footer crossover (page SEO seulement) */}
      {isPage && (
        <div className="mt-12 border-t border-border/60 pt-8 flex flex-wrap items-center justify-between gap-4 text-sm font-sans">
          <Link to="/" className="text-foreground/70 hover:text-primary transition-colors">
            {isFr ? '← Retour à l\'accueil' : '← Back to home'}
          </Link>
          <div className="flex gap-6">
            <Link to="/lexique" className="text-foreground/70 hover:text-primary transition-colors">
              {isFr ? 'Lexique →' : 'Glossary →'}
            </Link>
            <Link to="/colophon" className="text-foreground/70 hover:text-primary transition-colors">
              {isFr ? 'Colophon →' : 'Colophon →'}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
